/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderNode from "App/render/webgl/nodes/render_node";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import RenderObject from "App/geometry/objects/object";
import Factory from "App/render/webgl/nodes/factory";
import Pass from "App/render/webgl/passes/pass";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import {BatchesManagerNodeTypeName, ShapeBatchNodeTypeName} from "App/render/webgl/nodes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class BatchesManagerNode extends RenderNode {

    public static TypeName: string = BatchesManagerNodeTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode) {
        super(aSceneAPI, aRenderGraph, aParentNode, null);

        this._typeName = BatchesManagerNode.TypeName;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        return new BatchesManagerNode(aSceneAPI, aRenderGraph, aParentNode);
    }

    public compareProperties(aShape: RenderObject): boolean {
        return true; // we have always only one batch selector node
    }

    /***
     * Look to children batches and ask fo free space for the input shape
     * @paam aShape
     * @returns {boolean}
     */
    public addShape(aShape: RenderObject): boolean {
        let shapeBatchNode = null;
        for (let i = 0; i !== this.childnodes.length; i++) {
            if ((this.childnodes[i] as ShapeBatchNode).canAcceptShape(aShape)) {
                shapeBatchNode = this.childnodes[i];
                break;
            }
        }

        if (!shapeBatchNode) {
            shapeBatchNode = Factory.create(ShapeBatchNodeTypeName, this._sceneAPI, this._renderGraph, this, aShape);
            this.childnodes.push(shapeBatchNode);
        }

        // save reference to the batch node for each shape
        aShape.attachToRenderGraphObject(shapeBatchNode);

        shapeBatchNode.addShape(aShape);

        return true;
    }

    /***
     * Find the removed shape and mark the space in the appropriate buffer as free space
     * @param aShape
     * @returns {boolean}
     */
    public removeShape(aShape: RenderObject): boolean {
        for (let child of this.childnodes) {
            if (child.removeShape(aShape)) {
                return true;
            }
        }

        return false;
    }

    /***
     * Pass through all child nodes and move the free space in buffers to the end step by step. When new shape arrive, then
     * we just use the free space to
     * @param aRenderPass
     */
    public render(aRenderPass: Pass): boolean {
        this._renderReturnVal = false;
        for (let i = 0; i !== this.childnodes.length; i++) {
            this._renderReturnVal = this.childnodes[i].render(aRenderPass) || this._renderReturnVal;
        }

        return this._renderReturnVal;
    }
}

Factory.register(BatchesManagerNode);
